'use strict'

export const AUDIO_STATUS = {
    UNKNOWN: 'unknown',
    PRESENT: 'present',
    ABSENT: 'absent'
}

export const MP4_PROBE_MAX_BYTES = 1024 * 1024
export const AUDIO_PROBE_MAX_BYTES = 512 * 1024
// Matches SFRA's checkAudioForSrc: a track only counts as having sound when its
// RMS clears this. Measuring RMS (rather than peak samples) is what stops a
// faint/noisy-but-inaudible track from showing the sound button.
export const AUDIBLE_RMS_THRESHOLD = 0.001

const AUDIO_STATUS_CACHE_MAX = 100
const audioStatusCache = new Map()
const audibleSignalInFlight = new Map()
const audibleSignalQueue = []
const AUDIO_DECODE_CONCURRENCY = 2
let activeAudioDecodes = 0

const cacheAudioStatus = (src, status) => {
    if (!src || status === AUDIO_STATUS.UNKNOWN) return

    audioStatusCache.delete(src)
    audioStatusCache.set(src, status)

    if (audioStatusCache.size > AUDIO_STATUS_CACHE_MAX) {
        audioStatusCache.delete(audioStatusCache.keys().next().value)
    }
}

export const getCachedAudioStatus = (src) =>
    src && audioStatusCache.has(src) ? audioStatusCache.get(src) : AUDIO_STATUS.UNKNOWN

export const setCachedAudioStatus = (src, status) => cacheAudioStatus(src, status)

const runQueuedAudioDecode = () => {
    if (activeAudioDecodes >= AUDIO_DECODE_CONCURRENCY || !audibleSignalQueue.length) return

    const task = audibleSignalQueue.shift()
    activeAudioDecodes += 1

    task.job()
        .then((status) => task.resolve(status))
        .catch(() => task.resolve(AUDIO_STATUS.UNKNOWN))
        .finally(() => {
            activeAudioDecodes -= 1
            runQueuedAudioDecode()
        })
}

const queueAudioDecode = (job) =>
    new Promise((resolve) => {
        audibleSignalQueue.push({job, resolve})
        runQueuedAudioDecode()
    })

export const getHlsManifestAudioStatus = (data = {}) => {
    const hasAlternateAudio = Array.isArray(data.audioTracks) && data.audioTracks.length > 0
    const hasMuxedAudio =
        Array.isArray(data.levels) && data.levels.some((level) => Boolean(level?.audioCodec))
    const hasDeclaredVideo =
        Array.isArray(data.levels) && data.levels.some((level) => Boolean(level?.videoCodec))

    if (hasAlternateAudio || hasMuxedAudio) return AUDIO_STATUS.UNKNOWN
    if (hasDeclaredVideo) return AUDIO_STATUS.ABSENT

    return AUDIO_STATUS.UNKNOWN
}

export const getHlsBufferAudioStatus = (data = {}) => {
    const tracks = data.tracks || data
    if (!tracks || typeof tracks !== 'object') return AUDIO_STATUS.UNKNOWN
    if (tracks.audio || tracks.audiovideo) return AUDIO_STATUS.UNKNOWN
    if (tracks.video) return AUDIO_STATUS.ABSENT
    return AUDIO_STATUS.UNKNOWN
}

const readBox = (bytes, offset, limit) => {
    if (offset < 0 || offset + 8 > limit) return null

    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
    let size = view.getUint32(offset)
    const type = String.fromCharCode(
        bytes[offset + 4],
        bytes[offset + 5],
        bytes[offset + 6],
        bytes[offset + 7]
    )
    let headerSize = 8

    if (size === 1) {
        if (offset + 16 > limit) return null
        const high = view.getUint32(offset + 8)
        const low = view.getUint32(offset + 12)
        size = high * 2 ** 32 + low
        headerSize = 16
    } else if (size === 0) {
        size = limit - offset
    }

    if (!Number.isSafeInteger(size) || size < headerSize || offset + size > limit) return null

    return {type, start: offset, end: offset + size, contentStart: offset + headerSize}
}

const findChildBoxes = (bytes, parent, type) => {
    const matches = []
    let offset = parent.contentStart

    while (offset < parent.end) {
        const box = readBox(bytes, offset, parent.end)
        if (!box) break
        if (box.type === type) matches.push(box)
        offset = box.end
    }

    return matches
}

const findCompleteMoovBox = (bytes) => {
    for (let index = 4; index + 4 <= bytes.length; index++) {
        if (
            bytes[index] === 0x6d &&
            bytes[index + 1] === 0x6f &&
            bytes[index + 2] === 0x6f &&
            bytes[index + 3] === 0x76
        ) {
            const box = readBox(bytes, index - 4, bytes.length)
            if (box?.type === 'moov') return box
        }
    }

    return null
}

export const getMp4BufferAudioStatus = (buffer) => {
    if (!buffer?.byteLength) return AUDIO_STATUS.UNKNOWN

    const bytes = new Uint8Array(buffer)
    const moov = findCompleteMoovBox(bytes)
    if (!moov) return AUDIO_STATUS.UNKNOWN

    const tracks = findChildBoxes(bytes, moov, 'trak')
    if (!tracks.length) return AUDIO_STATUS.UNKNOWN

    let foundHandler = false

    for (const track of tracks) {
        const media = findChildBoxes(bytes, track, 'mdia')[0]
        if (!media) continue

        const handler = findChildBoxes(bytes, media, 'hdlr')[0]
        if (!handler || handler.contentStart + 12 > handler.end) continue

        foundHandler = true

        const handlerTypeOffset = handler.contentStart + 8
        const handlerType = String.fromCharCode(
            bytes[handlerTypeOffset],
            bytes[handlerTypeOffset + 1],
            bytes[handlerTypeOffset + 2],
            bytes[handlerTypeOffset + 3]
        )

        if (handlerType === 'soun') return AUDIO_STATUS.PRESENT
    }

    return foundHandler ? AUDIO_STATUS.ABSENT : AUDIO_STATUS.UNKNOWN
}

const fetchMp4Range = async (src, range, signal) => {
    try {
        const response = await fetch(src, {headers: {Range: range}, signal})
        if (!response.ok && response.status !== 206) return null

        const buffer = await response.arrayBuffer()
        if (!buffer.byteLength) return null
        if (buffer.byteLength <= MP4_PROBE_MAX_BYTES) return buffer

        return range.startsWith('bytes=-')
            ? buffer.slice(buffer.byteLength - MP4_PROBE_MAX_BYTES)
            : buffer.slice(0, MP4_PROBE_MAX_BYTES)
    } catch {
        return null
    }
}

export const detectMp4AudioStatus = (src, signal) => {
    const cachedStatus = getCachedAudioStatus(src)
    if (cachedStatus !== AUDIO_STATUS.UNKNOWN) return Promise.resolve(cachedStatus)

    return (async () => {
        const firstBytes = await fetchMp4Range(src, `bytes=0-${MP4_PROBE_MAX_BYTES - 1}`, signal)
        let status = getMp4BufferAudioStatus(firstBytes)

        if (status === AUDIO_STATUS.UNKNOWN && !signal?.aborted) {
            const lastBytes = await fetchMp4Range(src, `bytes=-${MP4_PROBE_MAX_BYTES}`, signal)
            status = getMp4BufferAudioStatus(lastBytes)
        }

        // The container check above only proves an audio TRACK exists — a track
        // can be present and still be silent, so mirroring SFRA's
        // `checkAudioForSrc` we decode the fetched chunk and measure its RMS.
        //
        // But decodeAudioData can't parse every mp4: Chromium rejects many
        // files that Firefox decodes fine (crbug.com/460678756), which surfaces
        // here as UNKNOWN. So ONLY a decode that actually ran and measured
        // silence may downgrade the track to ABSENT. When the decode couldn't
        // run we keep the container result — hiding the sound control on an
        // audible video is worse than showing a no-op control on a silent one.
        if (status === AUDIO_STATUS.PRESENT && firstBytes && !signal?.aborted) {
            const audibleStatus = await detectAudibleSignal(firstBytes, src)
            if (audibleStatus === AUDIO_STATUS.ABSENT) status = AUDIO_STATUS.ABSENT
        }

        cacheAudioStatus(src, status)
        return status
    })()
}

export const detectAudibleSignal = async (buffer, cacheKey) => {
    if (!buffer?.byteLength) return AUDIO_STATUS.UNKNOWN

    const AudioContextClass = window.AudioContext || window.webkitAudioContext
    if (!AudioContextClass) return AUDIO_STATUS.UNKNOWN

    if (cacheKey && audibleSignalInFlight.has(cacheKey)) {
        return audibleSignalInFlight.get(cacheKey)
    }

    const decodePromise = queueAudioDecode(async () => {
        const audioContext = new AudioContextClass()

        try {
            const decoded = await audioContext.decodeAudioData(buffer.slice(0))
            if (!decoded?.numberOfChannels) return AUDIO_STATUS.UNKNOWN

            // SFRA's checkAudioForSrc measurement: RMS per channel, take the
            // loudest, and require it to clear AUDIBLE_RMS_THRESHOLD. Silence
            // (all zeros) and near-silent noise both fall to ABSENT.
            let maxRms = 0
            for (let channel = 0; channel < decoded.numberOfChannels; channel++) {
                const samples = decoded.getChannelData(channel)
                let sumSquares = 0
                for (let index = 0; index < samples.length; index++) {
                    sumSquares += samples[index] * samples[index]
                }
                const rms = samples.length ? Math.sqrt(sumSquares / samples.length) : 0
                if (rms > maxRms) maxRms = rms
            }

            return maxRms > AUDIBLE_RMS_THRESHOLD ? AUDIO_STATUS.PRESENT : AUDIO_STATUS.ABSENT
        } catch {
            return AUDIO_STATUS.UNKNOWN
        } finally {
            audioContext.close()
        }
    })

    if (cacheKey) {
        audibleSignalInFlight.set(cacheKey, decodePromise)
        decodePromise.finally(() => audibleSignalInFlight.delete(cacheKey))
    }

    return decodePromise
}

export const getMediaElementAudioStatus = (video) => {
    if (!video) return AUDIO_STATUS.UNKNOWN

    if (video.audioTracks && typeof video.audioTracks.length === 'number') {
        return video.audioTracks.length === 0 ? AUDIO_STATUS.ABSENT : AUDIO_STATUS.UNKNOWN
    }

    if (typeof video.mozHasAudio === 'boolean') {
        return video.mozHasAudio ? AUDIO_STATUS.UNKNOWN : AUDIO_STATUS.ABSENT
    }

    return AUDIO_STATUS.UNKNOWN
}